<script setup lang="ts">
import { DocumentState } from "~/types/enums/DocumentState";

const props = defineProps({
  submittedDocument: {
    type: Object as PropType<SubmittedDocument>,
    required: true,
  },
});

const emit = defineEmits(["delete"]);

const formatDate = (timestamp: number) => {
  if (!timestamp) return "";
  return new Date(timestamp).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

const getStateConfig = (state: DocumentState) => {
  switch (state) {
    case DocumentState.graded:
      return {
        label: "Graded",
        color: "bg-success/10 text-success border-success/20",
      };
    case DocumentState.grading:
      return {
        label: "Grading",
        color: "bg-warning/10 text-warning border-warning/20",
      };
    case DocumentState.submitted:
    case DocumentState.studentCompleted:
      return {
        label: "Completed",
        color: "bg-success/10 text-success border-success/20",
      };
    case DocumentState.studentReview:
      return {
        label: "Review",
        color: "bg-secondary/10 text-secondary border-secondary/20",
      };
    case DocumentState.editing:
      return {
        label: "Draft",
        color: "bg-medium/10 text-medium border-medium/20",
      };

    default:
      return { label: state, color: "bg-light text-medium border-light-shade" };
  }
};

const checklistStore = useUserChecklistsStore();
const { checklists } = storeToRefs(checklistStore);

const matchingChecklist = computed(() => {
  return checklists.value.find(
    (c) => c.id === props.submittedDocument?.checklistId
  );
});

const controller = useNewDialogController();
const openMoreActions = () => {
  controller.value.open();
};

const closeMoreActions = () => {
  controller.value.close();
};

const isDeleting = ref(false);
const deleteDocument = async () => {
  if (!props.submittedDocument.id) return;

  const response = await useBaseConfirm(
    "Delete Document",
    "Are you sure you want to delete this document?"
  );

  if (!response) return;

  emit("delete", props.submittedDocument.id);
};
</script>

<template>
  <NuxtLink
    :to="useCreateCompanionPortalRoute(`/${submittedDocument.id}`)"
    class="block mb-2 group"
    active-class="active-document"
  >
    <div
      class="p-3 rounded-xl border transition-all duration-200 flex items-start gap-3 relative overflow-hidden group-hover:bg-white group-hover:border-gray-300 group-hover:shadow-sm"
      :class="[
        $route.params.documentId === submittedDocument.id
          ? 'bg-white border-primary/30 shadow-sm ring-1 ring-primary/20'
          : 'bg-transparent border-transparent',
      ]"
    >
      <!-- Icon -->
      <div
        class="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-colors"
        :class="[
          $route.params.documentId === submittedDocument.id
            ? 'bg-primary/10 text-primary'
            : 'bg-gray-100 text-gray-500 group-hover:bg-gray-50 group-hover:text-primary',
        ]"
      >
        <BaseIconNotebookText class="w-5 h-5" />
      </div>

      <!-- Content -->
      <div class="flex-1 min-w-0">
        <div class="flex justify-between items-start">
          <h3
            class="font-medium text-sm text-gray-900 truncate pr-8 transition-colors"
            :class="{
              'text-primary': $route.params.documentId === submittedDocument.id,
            }"
          >
            {{ submittedDocument.name || "Untitled Document" }}
          </h3>
        </div>

        <div class="flex items-center gap-2 mt-1">
          <span class="text-xs text-gray-500 truncate">
            {{
              formatDate(
                submittedDocument.lastUpdatedTimestamp ||
                  submittedDocument.createOnTimestamp
              )
            }}
          </span>

          <!-- Status Badge -->
          <span
            v-if="submittedDocument.state"
            class="px-1.5 py-0.5 rounded text-[10px] font-medium border uppercase tracking-wider"
            :class="getStateConfig(submittedDocument.state).color"
          >
            {{ getStateConfig(submittedDocument.state).label }}
          </span>
        </div>

        <div
          v-if="matchingChecklist"
          class="mt-1 text-xs text-gray-400 truncate"
        >
          {{ matchingChecklist.title }}
        </div>
      </div>

      <!-- More Actions -->
      <BasePopover :controller="controller" class="">
        <BaseIconButton
          @click.stop.prevent="openMoreActions"
          class="group-hover:opacity-100 opacity-0 transition-all duration-200"
        >
          <BaseIconMoreVert />
        </BaseIconButton>
        <template #content>
          <div class="flex flex-col p-1">
            <div
              class="flex items-center gap-2 w-[100px] hover:bg-danger/10 rounded-md p-2 cursor-pointer"
              @click="deleteDocument"
            >
              <BaseIconDelete class="w-4 h-4 text-danger" />
              <span class="text-danger text-body-2"> Delete </span>
            </div>
          </div>
        </template>
      </BasePopover>
    </div>
  </NuxtLink>
</template>

<style scoped>
/* Optional: Add any specific transitions or overrides here */
</style>
